// https://developer.mozilla.org/en-US/docs/Web/API/Permissions_API#browser_compatibility

import { EventEmitter } from 'eventemitter3';
import { DeviceType } from '../enums';
import loggerManager from '../utils/log/logger-manager';
import Logger from '../utils/log/logger';

export const enum PermissionEvent {
  PERMISSION_STATE_CHANGE = 'permission-state-change'
}

class PermissionManager extends EventEmitter {
  states: Record<string, PermissionStatus | null> = {};
  private permissionChangeHandler: () => void;
  log: Logger;
  constructor() {
    super();
    this.log = loggerManager.createLogger({ id: 'pm' });
    this.permissionChangeHandler = () => {
      this.emit(PermissionEvent.PERMISSION_STATE_CHANGE, {
        camera: this.states.camera?.state,
        microphone: this.states.microphone?.state
      });
    };
  }

  async request(names: DeviceType[]): Promise<void> {
    this.log.info(`request ${names.join(', ')}`);
    if (names.length === 0) {
      return Promise.resolve();
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      video: names.includes(DeviceType.CAMERA),
      audio: names.includes(DeviceType.MICROPHONE)
    });
    stream.getTracks().forEach(track => track.stop());
  }

  async get(name: DeviceType): Promise<PermissionState | null> {
    try {
      if (!this.states[name]) {
        this.states[name] = await navigator.permissions.query({ name });
        this.states[name].addEventListener('change', this.permissionChangeHandler);
      }
      this.log.info(`get ${name} permission state: ${this.states[name].state}`);
      return this.states[name].state;
    } catch (error) {
      this.log.error(`get ${name} permission failed, error: ${error instanceof Error ? error.message : error}`);
      return null;
    }
  }

  destroy() {
    Object.values(this.states).forEach(state => {
      state?.removeEventListener('change', this.permissionChangeHandler);
    });
    this.states = {};
  }
}

export default new PermissionManager();
